import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Award, Globe, BookOpen, Heart } from 'lucide-react';

export const About = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const features = [
    { icon: Globe, key: 'international' },
    { icon: BookOpen, key: 'bilingual' },
    { icon: Award, key: 'certified' },
    { icon: Heart, key: 'personalized' },
  ];

  return (
    <section id="about" data-testid="about-section" ref={ref} className="py-16 lg:py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-amber-600 font-inter text-sm font-semibold tracking-wider uppercase">
                {t('about.tag')}
              </span>
              <span className="font-inter italic text-slate-500 text-xs sm:text-sm leading-relaxed">
                {t('about.tagBio')}
              </span>
            </div>
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 mb-6 leading-tight">
              {t('about.title1')} <span className="italic">{t('about.title1Italic')}</span>
              <br />
              {t('about.title2')} <span className="italic">{t('about.title2Italic')}</span>
            </h2>
            <div className="section-divider !ml-0 mb-8" />
            <p className="font-inter text-slate-600 text-lg leading-relaxed mb-6">{t('about.p1')}</p>
            <p className="font-inter text-slate-600 leading-relaxed">{t('about.p2')}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-slate-900 relative aspect-video">
                <video
                  data-testid="about-video"
                  src="https://customer-assets.emergentagent.com/job_2af900fb-a4c0-4108-9990-fef980765d9c/artifacts/k2gq01rk_Lafayette.mp4"
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                  poster="https://images.unsplash.com/photo-1731349219592-60ca16964631?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzY2hvb2wlMjBidWlsZGluZyUyMGFyY2hpdGVjdHVyZSUyMHN1bm55fGVufDB8fHx8MTc3MDU0MTU0NXww&ixlib=rb-4.1.0&q=85"
                >
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
              </div>
              <div className="bg-slate-900 rounded-2xl p-6 text-white">
                <div className="font-playfair text-3xl font-bold text-amber-400">{t('about.stat1')}</div>
                <div className="text-slate-300 text-sm mt-1">{t('about.stat1Label')}</div>
              </div>
              <div className="bg-amber-500 rounded-2xl p-6">
                <div className="font-playfair text-3xl font-bold text-slate-900">{t('about.stat2')}</div>
                <div className="text-slate-800 text-sm mt-1">{t('about.stat2Label')}</div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-20">
          {features.map((feature, index) => (
            <motion.div
              key={feature.key}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              data-testid={`feature-card-${index}`}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 card-hover"
            >
              <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="font-playfair text-lg font-semibold text-slate-900 mb-2">
                {t(`about.features.${feature.key}.title`)}
              </h3>
              <p className="font-inter text-slate-600 text-sm leading-relaxed">
                {t(`about.features.${feature.key}.description`)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
